import { useState } from 'react';
import { Activity as ActivityIcon } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { Pager } from '@/components/pager';
import { usePortalActivity } from '@/lib/portal-activity';
import { formatDateTime, formatStatus } from '@/lib/format';
import type { PortalActivityData } from '@/types/api';

export function PortalActivity() {
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = usePortalActivity(page);
  const rows = paginated?.data;
  const meta = paginated?.meta;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ActivityIcon}
        title="Activity"
        subtitle="A running log of what the Mehrutherm team has shared and sent you."
      />

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={ActivityIcon}
          title="No activity yet"
          description="Proposals, invoices, documents, and updates from Mehrutherm will show up here as they happen."
        />
      ) : (
        <div className="space-y-4">
          <ol className="relative space-y-4 border-l border-[var(--color-border)] pl-5">
            {rows.map((item) => (
              <ActivityItem key={item.id} item={item} />
            ))}
          </ol>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function ActivityItem({ item }: { item: PortalActivityData }) {
  return (
    <li className="relative">
      <span className="absolute -left-[1.4rem] top-1.5 size-2.5 rounded-full border-2 border-[var(--color-surface)] bg-[var(--color-primary)]" />
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-medium text-foreground">{item.title ?? formatStatus(item.type)}</span>
        {item.subject_label ? (
          <span className="rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {item.subject_label}
          </span>
        ) : null}
        <span className="text-xs text-muted-foreground">{formatDateTime(item.occurred_at)}</span>
      </div>
      {item.body ? (
        <p className="mt-0.5 whitespace-pre-line text-sm text-[var(--color-foreground-muted)]">{item.body}</p>
      ) : null}
    </li>
  );
}
